import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Button from '@/components/base/Button';
import { useEffect } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://cssbuyspreadsheet.blog';

const guiaSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  '@id': `${siteUrl}/guia-de-compras#article`,
  headline: 'Guia Completo de Compras CSSBuy Spreadsheet',
  description: 'Nove guias essenciais sobre cssbuy spreadsheet: marcas, frete, comparação de agentes, tendências, tamanhos, economia, produtos populares, guia para iniciantes e réplicas de qualidade.',
  image: 'https://readdy.ai/api/search-image?query=Organized%20bookshelf%20wall%20filled%20with%20fashion%20magazines%20style%20guides%20and%20product%20catalogs%20in%20modern%20library%2C%20knowledge%20and%20learning%20concept%2C%20warm%20ambient%20reading%20light%2C%20intellectual%20atmosphere%2C%20leather%20bound%20books%20and%20glossy%20magazines%2C%20sophisticated%20study%20space%2C%20rich%20wood%20tones%20and%20cream%20pages%2C%20editorial%20knowledge%20photography%2C%20quiet%20focused%20environment&width=1800&height=800&seq=gc-hero&orientation=landscape',
  author: {
    '@type': 'Organization',
    name: 'Equipe CSSBuy Spreadsheet Brasil',
    url: `${siteUrl}/#organization`,
  },
  publisher: {
    '@type': 'Organization',
    name: 'cssbuyspreadsheet.blog',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png',
    },
  },
  datePublished: '2026-06-27',
  dateModified: '2026-06-27',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}/guia-de-compras`,
  },
  inLanguage: 'pt-BR',
  isAccessibleForFree: true,
};

const guiaBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Início',
      item: `${siteUrl}/`,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Guia de Compras',
      item: `${siteUrl}/guia-de-compras`,
    },
  ],
};

const sections = [
  {
    id: 'melhores-marcas',
    title: 'Melhores Marcas para Comprar no CSSBuy',
    tag: 'Marcas',
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=Luxury%20and%20streetwear%20brand%20logos%20displayed%20as%20artistic%20gallery%20wall%20in%20modern%20creative%20studio%2C%20fashion%20brand%20showcase%20concept%2C%20warm%20gallery%20lighting%2C%20minimalist%20frames%2C%20high%20end%20aesthetic%2C%20editorial%20brand%20photography%2C%20sophisticated%20art%20gallery%20atmosphere%2C%20cream%20walls%20with%20brass%20accents%2C%20curated%20brand%20collection%20display&width=1200&height=500&seq=gc-marcas&orientation=landscape',
    content: [
      'Quando se trata de compras internacionais usando o <strong>cssbuy spreadsheet</strong>, conhecer as melhores marcas faz toda a diferença. O spreadsheet reúne produtos das marcas mais desejadas do mercado, desde streetwear até luxo acessível, todas organizadas e com links verificados pela nossa equipe.',
      'Entre as marcas mais populares no <strong>cssbuy spreadsheet brasil</strong>, destacam-se nomes como Nike, Adidas, Supreme, Balenciaga, Off-White, Gucci, Louis Vuitton e muitas outras. O diferencial do nosso spreadsheet é que cada link passa por uma verificação manual antes de ser adicionado, garantindo que você encontre produtos de qualidade e evite falsificações grosseiras.',
      'Além das marcas globais, o <strong>cssbuy spreadsheet 2026</strong> também inclui marcas asiáticas em ascensão que estão ganhando popularidade entre compradores brasileiros. Marcas como Li-Ning, Peacebird e Bosideng oferecem qualidade excepcional a preços muito competitivos, sendo ótimas alternativas para quem busca custo-benefício.',
      'Nossa recomendação é sempre verificar as avaliações da comunidade antes de finalizar qualquer compra. O <strong>cssbuy spreadsheet</strong> inclui notas e comentários de outros compradores, ajudando você a tomar decisões mais informadas sobre quais marcas e produtos realmente valem o investimento.',
    ],
  },
  {
    id: 'frete-brasil',
    title: 'Frete para o Brasil: Guia Completo de Envios',
    tag: 'Frete',
    readTime: '7 min',
    image: 'https://readdy.ai/api/search-image?query=World%20map%20with%20Brazil%20highlighted%20and%20shipping%20routes%20visualized%20with%20dotted%20lines%20and%20miniature%20cargo%20ships%2C%20global%20logistics%20concept%2C%20modern%20clean%20office%20desk%20setting%2C%20warm%20professional%20lighting%2C%20international%20trade%20theme%2C%20organized%20shipping%20documentation%2C%20creative%20geography%20visualization%2C%20corporate%20logistics%20photography%2C%20blue%20green%20and%20warm%20wood%20tones&width=1200&height=500&seq=gc-frete&orientation=landscape',
    content: [
      'Uma das maiores dúvidas de quem usa o <strong>cssbuy spreadsheet</strong> é sobre como funciona o frete para o Brasil. A boa notícia é que existem diversas opções de envio, cada uma com suas vantagens em termos de custo, prazo e segurança. Entender essas opções é fundamental para fazer compras inteligentes.',
      'As principais modalidades de frete disponíveis no <strong>cssbuy spreadsheet</strong> incluem: China Post (mais econômico, prazo de 30-60 dias), EMS/ePacket (intermediário, 15-30 dias), DHL/FedEx (rápido, 5-10 dias) e linhas de frete marítimo para pedidos maiores. Cada opção tem um equilíbrio diferente entre custo e velocidade.',
      'Um ponto importante que todo comprador brasileiro precisa saber: produtos importados estão sujeitos à tributação alfandegária. Usando o <strong>cssbuy spreadsheet brasil</strong>, você encontra dicas atualizadas sobre como declarar valores, quais produtos têm maior probabilidade de taxação e estratégias para minimizar custos adicionais.',
      'Recomendamos fortemente que você utilize os serviços de agente de compras integrados ao <strong>cssbuy spreadsheet</strong>, pois eles oferecem opções de consolidação de pacotes (juntar vários pedidos em uma única remessa) e seguro de entrega, proporcionando mais segurança e economia no processo de envio para o Brasil.',
    ],
  },
  {
    id: 'cssbuy-vs-agentes',
    title: 'CSSBuy vs Outros Agentes de Compra',
    tag: 'Comparação',
    readTime: '5 min',
    image: 'https://readdy.ai/api/search-image?query=Two%20smartphone%20screens%20side%20by%20side%20showing%20different%20shopping%20apps%20interface%20comparison%2C%20modern%20tech%20review%20concept%2C%20clean%20studio%20lighting%2C%20product%20comparison%20theme%2C%20minimal%20desk%20setup%20with%20coffee%2C%20professional%20tech%20reviewer%20workspace%2C%20neutral%20background%2C%20balanced%20composition%2C%20decision%20making%20visualization%2C%20warm%20grey%20and%20white%20tones&width=1200&height=500&seq=gc-agentes&orientation=landscape',
    content: [
      'O mercado de agentes de compra chineses é competitivo, e o <strong>cssbuy spreadsheet</strong> foi desenvolvido para funcionar perfeitamente com diversos agentes. Mas como escolher o melhor? Nossa análise compara CSSBuy com outros agentes populares como Wegobuy, Sugargoo e Pandabuy para ajudar na sua decisão.',
      'O CSSBuy se destaca por oferecer uma das taxas de serviço mais competitivas do mercado (em média 4-6%), além de fotos de inspeção gratuitas para cada produto. Isso significa que, ao usar o <strong>cssbuy spreadsheet</strong> com CSSBuy, você economiza nas taxas e ainda recebe fotos reais dos produtos antes do envio.',
      'Em termos de qualidade de serviço, nossa pesquisa com a comunidade do <strong>cssbuy spreadsheet 2026</strong> mostra que o suporte ao cliente do CSSBuy é um dos mais ágeis, com tempo médio de resposta de 4 horas. Outros agentes podem levar até 24 horas para responder, o que pode ser frustrante quando você precisa de ajuda rápida.',
      'Independentemente do agente escolhido, o <strong>cssbuy spreadsheet</strong> é compatível com todos eles. Basta copiar o link do produto desejado e colar na plataforma do seu agente preferido. Nossa recomendação: experimente diferentes agentes para pedidos pequenos e veja qual atende melhor às suas necessidades específicas.',
    ],
  },
  {
    id: 'tendencias-2026',
    title: 'Tendências de Streetwear para 2026',
    tag: 'Tendências',
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=Fashion%20trend%20forecasting%20moodboard%20with%20fabric%20swatches%20color%20palette%20cards%20and%20runway%20photos%20on%20creative%20studio%20wall%2C%20fashion%20design%20concept%2C%20natural%20daylight%20from%20large%20windows%2C%20artistic%20creative%20direction%20workspace%2C%20textile%20samples%20and%20magazine%20tearsheets%2C%20trendy%20aesthetic%2C%20warm%20creative%20atmosphere%2C%20fashion%20industry%20photography%2C%20organized%20yet%20artistic%20composition&width=1200&height=500&seq=gc-tendencias&orientation=landscape',
    content: [
      'O mundo do streetwear está em constante evolução, e o <strong>cssbuy spreadsheet</strong> é a ferramenta ideal para acompanhar as tendências mais quentes de 2026. Nossa equipe monitora diariamente as principais plataformas e comunidades para identificar o que está realmente bombando.',
      'Entre as principais tendências identificadas no <strong>cssbuy spreadsheet 2026</strong>, destacam-se: o retorno das silhuetas oversized com cortes assimétricos, a popularidade crescente de tecidos técnicos e sustentáveis, o boom dos acessórios statement (bolsas maxi, cintos largos, óculos geométricos) e a fusão entre sportswear e alfaiataria casual.',
      'No <strong>cssbuy spreadsheet brasil</strong>, notamos um aumento significativo na procura por tênis chunky e color blocking, hoodies com estampas gráficas maximalistas, jaquetas utilitárias com múltiplos bolsos e conjuntos coordenados em tons terrosos e pastéis. Essas tendências refletem o que a comunidade brasileira está efetivamente comprando.',
      'O mais interessante é que o <strong>cssbuy spreadsheet</strong> permite que você acesse essas tendências muito antes delas chegarem ao varejo tradicional brasileiro. Enquanto as lojas locais ainda estão na temporada anterior, você já pode estar vestindo o que há de mais atual no streetwear global, tudo com preços acessíveis através dos agentes de compra.',
    ],
  },
  {
    id: 'guia-tamanhos',
    title: 'Guia de Tamanhos: Como Acertar na Compra',
    tag: 'Tamanhos',
    readTime: '7 min',
    image: 'https://readdy.ai/api/search-image?query=Measuring%20tape%20and%20size%20chart%20next%20to%20various%20clothing%20items%20on%20tailor%20workbench%2C%20fashion%20fitting%20concept%2C%20bright%20clean%20tailor%20shop%20atmosphere%2C%20professional%20alteration%20tools%2C%20fabric%20samples%20and%20measuring%20instruments%2C%20organized%20garment%20measurements%2C%20warm%20professional%20lighting%2C%20precise%20tailoring%20photography%2C%20cream%20and%20wood%20workspace&width=1200&height=500&seq=gc-tamanhos&orientation=landscape',
    content: [
      'Acertar o tamanho é um dos maiores desafios para quem usa o <strong>cssbuy spreadsheet</strong> para comprar roupas e calçados da China. Os padrões asiáticos são diferentes dos brasileiros, e um erro na escolha do tamanho pode resultar em produtos que não servem. Mas com as dicas certas, você nunca mais vai errar.',
      'A regra de ouro do <strong>cssbuy spreadsheet</strong> para tamanhos é: nunca confie apenas na letra (P, M, G). Sempre verifique a tabela de medidas em centímetros fornecida pelo vendedor. Meça uma peça similar que você já tem e que veste bem, e compare com as medidas da tabela. Essa simples prática elimina 90% dos erros de tamanho.',
      'Para tênis e calçados no <strong>cssbuy spreadsheet 2026</strong>, a recomendação é pedir sempre um número acima do seu tamanho brasileiro habitual. A forma asiática tende a ser mais estreita, e a margem extra garante conforto. Se possível, meça o comprimento do seu pé em centímetros e compare com a tabela do vendedor — é o método mais preciso.',
      'O <strong>cssbuy spreadsheet brasil</strong> também inclui comentários da comunidade sobre o sizing de cada produto. Muitos compradores compartilham suas medidas e o tamanho que pediram, criando uma base de dados valiosa para você tomar decisões informadas. Antes de comprar, sempre leia esses comentários — eles são ouro puro para evitar devoluções e frustrações.',
    ],
  },
  {
    id: 'como-economizar',
    title: 'Como Economizar nas Compras Internacionais',
    tag: 'Economia',
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=Shopping%20cart%20filled%20with%20discount%20tags%20and%20coupons%20on%20bright%20desk%20with%20calculator%2C%20smart%20saving%20concept%2C%20cheerful%20morning%20light%2C%20money%20saving%20theme%2C%20organized%20budget%20planning%2C%20colorful%20sale%20tags%20and%20receipt%2C%20personal%20finance%20lifestyle%20photography%2C%20warm%20optimistic%20atmosphere%2C%20clean%20modern%20home%20office%2C%20green%20plant%20accent&width=1200&height=500&seq=gc-economizar&orientation=landscape',
    content: [
      'Economizar dinheiro é uma das principais razões pelas quais as pessoas recorrem ao <strong>cssbuy spreadsheet</strong>. Mas além dos preços já competitivos dos produtos, existem várias estratégias que podem aumentar ainda mais sua economia. Compartilhamos aqui as melhores práticas da comunidade.',
      'A primeira dica de ouro do <strong>cssbuy spreadsheet 2026</strong> é: consolide seus pedidos. Em vez de enviar cada produto separadamente, acumule vários itens no armazém do agente e envie tudo junto. Isso reduz drasticamente o custo do frete por produto. Alguns agentes oferecem até 60 dias de armazenamento gratuito, tempo mais que suficiente para montar um pacote econômico.',
      'Outra estratégia poderosa disponível no <strong>cssbuy spreadsheet brasil</strong> é aproveitar promoções sazonais chinesas. Datas como o Singles Day (11/11), Chinese New Year e 618 Festival oferecem descontos de até 70% em milhares de produtos. O spreadsheet é atualizado durante essas promoções com os melhores links e ofertas verificadas.',
      'Por fim, não subestime o poder da negociação. Muitos vendedores nas plataformas chinesas estão abertos a oferecer descontos para compras em quantidade. Use o <strong>cssbuy spreadsheet</strong> para encontrar produtos similares de diferentes vendedores e use isso como argumento para negociar. A comunidade frequentemente compartilha quais vendedores são mais flexíveis com preços.',
    ],
  },
  {
    id: 'produtos-populares',
    title: 'Produtos Mais Comprados pela Comunidade',
    tag: 'Populares',
    readTime: '5 min',
    image: 'https://readdy.ai/api/search-image?query=Bestselling%20fashion%20products%20displayed%20on%20illuminated%20pedestals%20in%20modern%20showroom%2C%20product%20showcase%20concept%2C%20dramatic%20gallery%20lighting%2C%20premium%20retail%20display%2C%20award%20winning%20items%20spotlight%2C%20dark%20background%20with%20products%20glowing%2C%20sophisticated%20exhibition%20photography%2C%20luxury%20product%20presentation%2C%20golden%20accents%2C%20exclusive%20collection%20atmosphere&width=1200&height=500&seq=gc-populares&orientation=landscape',
    content: [
      'O <strong>cssbuy spreadsheet</strong> não é apenas uma lista de produtos — é um reflexo do que a comunidade brasileira está realmente comprando. Analisando os dados de acesso e compras, identificamos os produtos mais populares que você precisa conhecer.',
      'No topo da lista de mais comprados do <strong>cssbuy spreadsheet 2026</strong> estão os tênis — de longe a categoria campeã. Modelos como Dunk Low, Air Force 1, Jordan 4 e Yeezy 350 dominam as buscas. A qualidade das réplicas melhorou tanto que muitos compradores afirmam ser indistinguíveis dos originais, mas por uma fração do preço.',
      'As camisetas oversized com estampas gráficas são o segundo item mais procurado no <strong>cssbuy spreadsheet brasil</strong>. Marcas como Fear of God Essentials, Represent e Balenciaga lideram essa categoria. O que chama atenção é o excelente custo-benefício: camisetas com qualidade de tecido premium por valores equivalentes a marcas básicas nacionais.',
      'Completando o top 5 de mais comprados no <strong>cssbuy spreadsheet</strong>, temos: hoodies e moletons (perfeitos para o inverno brasileiro), acessórios como bonés e carteiras, e por fim, jaquetas e casacos. O que todos esses itens têm em comum? A combinação de qualidade elevada, preço acessível e a possibilidade de acessar tendências globais antes que cheguem ao mercado brasileiro.',
    ],
  },
  {
    id: 'guia-iniciantes',
    title: 'CSSBuy para Iniciantes: Seu Primeiro Pedido',
    tag: 'Iniciantes',
    readTime: '8 min',
    image: 'https://readdy.ai/api/search-image?query=Friendly%20hand%20reaching%20out%20to%20help%20another%20hand%20with%20smartphone%20showing%20shopping%20app%2C%20beginner%20guidance%20concept%2C%20warm%20caring%20atmosphere%2C%20bright%20modern%20setting%2C%20mentoring%20and%20learning%20theme%2C%20soft%20natural%20lighting%2C%20supportive%20gesture%2C%20educational%20technology%20moment%2C%20encouraging%20and%20accessible%20photography%2C%20warm%20skin%20tones%20against%20clean%20white%20background&width=1200&height=500&seq=gc-iniciantes&orientation=landscape',
    content: [
      'Se você nunca comprou da China usando o <strong>cssbuy spreadsheet</strong>, não se preocupe — este guia foi feito especialmente para você. Vamos acompanhar cada etapa do processo, desde o acesso ao spreadsheet até receber seu primeiro pedido em casa. É mais simples do que parece.',
      'O primeiro passo é acessar o <strong>cssbuy spreadsheet</strong> através dos botões "cssbuy sheet" ou "buy it now" em nossa página inicial. Você será direcionado para a plataforma com todas as categorias organizadas. Navegue pelas categorias, explore os produtos e escolha algo simples para seu primeiro pedido — recomendamos começar com uma camiseta ou acessório.',
      'Após escolher seu produto no <strong>cssbuy spreadsheet 2026</strong>, copie o link e crie uma conta gratuita no CSSBuy (ou no agente de sua preferência). Cole o link na plataforma, escolha tamanho e cor, e faça o pagamento. O agente comprará o produto para você e enviará fotos de inspeção quando ele chegar ao armazém.',
      'Com o produto aprovado no armazém, é hora de escolher o frete para o Brasil. O <strong>cssbuy spreadsheet brasil</strong> tem uma seção dedicada a dicas de envio. Para o primeiro pedido, recomendamos uma opção de frete com rastreamento, mesmo que custe um pouco mais. Acompanhe o código de rastreamento e, em algumas semanas, seu produto estará na sua porta. Simples assim!',
    ],
  },
  {
    id: 'replicas-qualidade',
    title: 'Réplicas de Qualidade: O que Observar Antes de Comprar',
    tag: 'Qualidade',
    readTime: '6 min',
    image: 'https://readdy.ai/api/search-image?query=Magnifying%20glass%20examining%20detailed%20fabric%20stitching%20and%20material%20texture%20of%20premium%20garment%20on%20inspection%20table%2C%20quality%20control%20concept%2C%20bright%20clean%20workshop%20lighting%2C%20attention%20to%20craftsmanship%2C%20precision%20inspection%20tools%2C%20fashion%20quality%20assurance%2C%20professional%20product%20examination%20photography%2C%20white%20industrial%20workspace%2C%20meticulous%20detail%20focus&width=1200&height=500&seq=gc-qualidade&orientation=landscape',
    content: [
      'O mercado de réplicas evoluiu drasticamente, e o <strong>cssbuy spreadsheet</strong> reflete essa evolução. Hoje, existem diferentes níveis de qualidade, desde réplicas básicas até as chamadas "1:1" (idênticas ao original). Saber identificar e escolher o nível certo é essencial para uma boa experiência de compra.',
      'No <strong>cssbuy spreadsheet 2026</strong>, utilizamos um sistema de classificação por estrelas baseado no feedback da comunidade. Produtos 5 estrelas são aqueles que passaram pelo escrutínio de múltiplos compradores experientes e foram aprovados em critérios como qualidade do material, precisão dos detalhes, fidelidade da estampa e durabilidade após uso.',
      'Ao navegar pelo <strong>cssbuy spreadsheet brasil</strong>, preste atenção especial às fotos de inspeção compartilhadas por outros compradores. Essas imagens mostram o produto real recebido, não as fotos promocionais do vendedor. Compare detalhes como costura, posicionamento de logos, qualidade do tecido e acabamento geral antes de tomar sua decisão.',
      'Uma dica valiosa da comunidade do <strong>cssbuy spreadsheet</strong>: para produtos de couro, tênis e bolsas, priorize sempre os lotes mais recentes (batches). Os fabricantes aprimoram constantemente seus processos, e um mesmo produto pode ter qualidade significativamente melhor em um lote mais novo. Verifique a data de atualização do link antes de comprar.',
    ],
  },
];

export default function GuiaComprasPage() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, []);

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-20 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Organized%20bookshelf%20wall%20filled%20with%20fashion%20magazines%20style%20guides%20and%20product%20catalogs%20in%20modern%20library%2C%20knowledge%20and%20learning%20concept%2C%20warm%20ambient%20reading%20light%2C%20intellectual%20atmosphere%2C%20leather%20bound%20books%20and%20glossy%20magazines%2C%20sophisticated%20study%20space%2C%20rich%20wood%20tones%20and%20cream%20pages%2C%20editorial%20knowledge%20photography%2C%20quiet%20focused%20environment&width=1800&height=800&seq=gc-hero&orientation=landscape"
            alt="Guia de Compras CSSBuy Spreadsheet"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-foreground-950"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-500/20 text-accent-300 text-xs font-semibold mb-6">
            <i className="ri-book-open-line"></i>
            Guia de Compras
          </span>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-background-50 mb-4">
            Guia Completo de Compras
            <br />
            CSSBuy Spreadsheet
          </h1>
          <p className="text-background-50/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
            Nove guias essenciais para você dominar as compras internacionais. Das melhores marcas às dicas de economia, tudo que você precisa saber sobre o cssbuy spreadsheet.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <span className="text-background-50/50 text-xs flex items-center gap-1.5">
              <i className="ri-article-line"></i>
              9 artigos
            </span>
            <span className="text-background-50/50 text-xs flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              ~60 min de leitura
            </span>
            <span className="text-background-50/50 text-xs flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              Equipe CSSBuy Brasil
            </span>
          </div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="py-10 bg-background-100 border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <p className="text-foreground-400 text-xs font-semibold mb-4">NAVEGUE PELOS GUIAS</p>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="px-4 py-2 rounded-full bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200/50 text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap border border-background-200"
              >
                {s.tag}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Articles */}
      <section className="py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {sections.map((section, idx) => (
            <article
              key={section.id}
              id={section.id}
              className={`${idx > 0 ? 'mt-16 pt-16 border-t border-background-200' : ''}`}
            >
              {/* Breadcrumb */}
              <nav aria-label="Breadcrumb" className="mb-6">
                <ol className="flex items-center gap-2 text-sm text-foreground-400">
                  <li>
                    <a href="/" className="hover:text-foreground-700 transition-colors duration-200">
                      <i className="ri-home-line"></i> Início
                    </a>
                  </li>
                  <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li>
                    <a href="/guia-de-compras" className="hover:text-foreground-700 transition-colors duration-200">
                      Guia de Compras
                    </a>
                  </li>
                  <li><i className="ri-arrow-right-s-line text-xs"></i></li>
                  <li className="text-foreground-700 font-medium truncate max-w-[200px]">{section.title}</li>
                </ol>
              </nav>

              {/* Tag + Meta */}
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold">
                  <i className="ri-bookmark-line text-xs"></i>
                  {section.tag}
                </span>
                <span className="text-foreground-400 text-xs flex items-center gap-1.5">
                  <i className="ri-timer-line"></i>
                  {section.readTime} de leitura
                </span>
              </div>

              {/* Title */}
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground-950 leading-tight mb-6">
                {section.title}
              </h2>

              {/* Image */}
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={section.image}
                  alt={section.title}
                  className="w-full object-cover object-top"
                  loading="lazy"
                />
              </div>

              {/* Content */}
              <div className="space-y-5 text-foreground-700 leading-relaxed">
                {section.content.map((paragraph, pIdx) => (
                  <p
                    key={pIdx}
                    className="text-base md:text-lg"
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
              </div>

              {/* Internal Links */}
              <div className="mt-8 p-5 bg-background-100 rounded-2xl">
                <p className="text-foreground-500 text-sm mb-3">
                  <i className="ri-links-line mr-1.5"></i>
                  <strong>Continue explorando:</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  <a href="/" className="text-foreground-600 hover:text-foreground-950 text-sm underline transition-colors duration-200">
                    ← Página Inicial
                  </a>
                  <a href="/cssbuy-spreadsheet" className="text-foreground-600 hover:text-foreground-950 text-sm underline transition-colors duration-200">
                    O que é CSSBuy Spreadsheet
                  </a>
                  {sections.filter(s => s.id !== section.id).slice(0, 3).map(s => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="text-foreground-600 hover:text-foreground-950 text-sm underline transition-colors duration-200"
                    >
                      {s.title.length > 35 ? s.title.slice(0, 35) + '...' : s.title}
                    </a>
                  ))}
                </div>
              </div>
            </article>
          ))}

          {/* Final CTA */}
          <div className="mt-20 bg-foreground-950 rounded-3xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-background-50 mb-3">
              Pronto para começar a comprar?
            </h2>
            <p className="text-background-50/60 text-sm md:text-base mb-6 max-w-lg mx-auto">
              Agora que você já conhece todos os segredos do cssbuy spreadsheet, é hora de colocar em prática.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                href="https://kaspreadsheet.com"
                variant="primary"
                size="lg"
                className="bg-background-50 text-foreground-950 hover:bg-background-100 gap-2"
              >
                cssbuy sheet
                <i className="ri-arrow-right-up-line text-lg"></i>
              </Button>
              <Button
                href="https://kaspreadsheet.com"
                variant="outline"
                size="lg"
                className="border-background-50/30 text-background-50 hover:border-background-50 hover:bg-background-50/10 gap-2"
              >
                buy it now
                <i className="ri-external-link-line text-lg"></i>
              </Button>
            </div>
          </div>

          {/* Back to top */}
          <div className="text-center mt-12">
            <a
              href="/"
              className="inline-flex items-center gap-2 text-foreground-500 hover:text-foreground-950 text-sm font-medium transition-colors duration-200"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar para a página inicial
            </a>
          </div>
        </div>
      </section>

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(guiaSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(guiaBreadcrumb),
        }}
      />

      <Footer />
    </main>
  );
}